import { Flex, Icon, Text } from '@chakra-ui/react'
import { ChevronsLeft, ChevronsRight, Settings } from 'lucide-react'
import React, { Key, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '~/components/ui/tooltip'
import {
  SiteNavigationCollapseTabSizes,
  useSiteNavigationCollapseTabStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigationCollapseTab.style'

interface SiteNavigationCollapseTabProps {
  id?: Key | undefined
  isCollapsed?: boolean
  isSelected?: boolean
  hideExpand?: boolean
  onCollapseClicked?: () => void
  onExpandClicked?: () => void
  onSettingsClicked?: (id?: Key | undefined) => void
}
export const SiteNavigationCollapseTab = ({
  id,
  isCollapsed,
  isSelected,
  hideExpand,
  onCollapseClicked,
  onExpandClicked,
  onSettingsClicked,
}: SiteNavigationCollapseTabProps) => {
  const { t } = useTranslation()
  const styles = useSiteNavigationCollapseTabStyle({
    size: isCollapsed
      ? SiteNavigationCollapseTabSizes.Collapsed
      : SiteNavigationCollapseTabSizes.Normal,
  })

  const toggleButtonRef = useRef<HTMLButtonElement>(null)
  const shouldFocusToggleRef = useRef(false)

  useEffect(() => {
    if (!shouldFocusToggleRef.current) {
      return
    }
    shouldFocusToggleRef.current = false
    toggleButtonRef.current?.focus()
  }, [isCollapsed])

  const handleCollapseClicked = () => {
    shouldFocusToggleRef.current = true
    onCollapseClicked?.()
  }

  const handleExpandClicked = () => {
    shouldFocusToggleRef.current = true
    onExpandClicked?.()
  }

  const settingsButton = (
    <Tooltip
      showArrow
      content={t('nav.settings', 'Settings')}
      positioning={{ placement: 'right' }}
    >
      <Flex asChild css={styles.settingsIconWrapper}>
        <button
          type="button"
          aria-label={t('nav.settings', 'Settings')}
          aria-current={isSelected ? 'page' : undefined}
          data-selected={isSelected ? '' : undefined}
          onClick={() => onSettingsClicked?.(id)}
        >
          <Icon
            css={styles.settingsIcon}
            className="collapse-tab-settings-icon"
          >
            <Settings size="100%" />
          </Icon>
        </button>
      </Flex>
    </Tooltip>
  )

  const collapseTabContent = useMemo(() => {
    if (!isCollapsed) {
      return (
        <>
          <Flex asChild css={styles.contentWrapper}>
            <button
              ref={toggleButtonRef}
              type="button"
              className="collapse-tab-content"
              onClick={handleCollapseClicked}
            >
              <Flex css={styles.arrowIconWrapper}>
                <Icon as={ChevronsLeft} css={styles.arrowIcon} />
              </Flex>
              <Text css={styles.text}>
                {t('baseSettings.collapse', 'Collapse')}
              </Text>
            </button>
          </Flex>
          {settingsButton}
        </>
      )
    }

    return (
      <>
        {settingsButton}
        {!hideExpand && (
          <Tooltip
            showArrow
            content={t('baseSettings.expand', 'Expand')}
            positioning={{ placement: 'right' }}
          >
            <Flex asChild css={styles.arrowIconWrapper}>
              <button
                ref={toggleButtonRef}
                type="button"
                aria-label={t('baseSettings.expand', 'Expand')}
                onClick={handleExpandClicked}
              >
                <Icon
                  as={ChevronsRight}
                  className="collapse-tab-arrow-icon"
                  css={styles.arrowIcon}
                />
              </button>
            </Flex>
          </Tooltip>
        )}
      </>
    )
  }, [
    isCollapsed,
    isSelected,
    hideExpand,
    onCollapseClicked,
    onExpandClicked,
    onSettingsClicked,
    t,
  ])

  return (
    <Flex css={styles.container} key={id}>
      {collapseTabContent}
    </Flex>
  )
}
